import { DaiStreamMetadata } from '../models/DaiStreamMetadata';
import { DaiStreamManagerBuilder } from '../DaiStreamManagerBuilder';
import { DaiStreamManager } from '../models/DaiStreamManager';
import { Logger } from '@quickplay/foundation';
import { SsaiAdRequest } from '../models/ssai/SsaiAdRequest';
import { VodSsaiAdsRequest } from '../models/ssai/vod/VodSsaiAdsRequest';
import { LiveSsaiAdsRequest } from '../models/ssai/live/LiveSsaiAdsRequest';
import { createAdPlaybackFailure, instanceOfLiveSSAIAdsRequest, instanceOfVodSSAIAdsRequest } from './util/AdUtil';

export function createDaiStreamManager(builder: DaiStreamManagerBuilder): DaiStreamManager {
  const logger: Logger = builder.configuredLogger;
  const imaSDK: any = builder.imaSDK;
  const videoElement: HTMLVideoElement = builder.videoElement;
  const adElement: Element = builder.adElement;
  const locale: string = builder.locale;
  const daiContentSourceId = builder.contentSourceId;
  const daiVideoId = builder.videoId;
  const daiAssetKey = builder.assetKey;
  const daiFormat = builder.format;
  const daiApiKey = builder.apiKey;
  const fallbackUrl = builder.fallback;

  let wrapperStreamManager: any = null;
  let fallbackData: any = null;
  let streamManager: any = null;
  let ssaiAdsRequest: SsaiAdRequest | null = null;

  function setupImaDai(resolve: any, reject: any): void {
    let settings = null;

    if (locale) {
      settings = new imaSDK.dai.api.UiSettings();
      settings.setLocale(locale);
    }

    if (settings) {
      streamManager = new imaSDK.dai.api.StreamManager(videoElement, adElement, settings);
    } else {
      streamManager = new imaSDK.dai.api.StreamManager(videoElement, adElement);
    }

    wrapperStreamManager = createWrapperStreamManager();

    streamManager.addEventListener(
      [imaSDK.dai.api.StreamEvent.Type.LOADED, imaSDK.dai.api.StreamEvent.Type.ERROR],
      (event: any) => onStreamManagerEvent(event, resolve),
      false,
    );

    const daiAdTagParameters = {
      ...builder.adTagParams,
      vpmute: videoElement.muted || videoElement.volume === 0 ? 1 : 0,
    };

    if (daiAssetKey) {
      const liveSsaiAdsRequest: LiveSsaiAdsRequest = {
        adTagUrl: '',
        slotHeight: 0,
        slotWidth: 0,
        assetKey: daiAssetKey,
        format: daiFormat,
        adTagParameters: daiAdTagParameters,
      };

      ssaiAdsRequest = liveSsaiAdsRequest;
    } else {
      const vodSsaiAdsRequest: VodSsaiAdsRequest = {
        adTagUrl: '',
        slotHeight: 0,
        slotWidth: 0,
        contentSourceId: daiContentSourceId,
        videoId: daiVideoId,
        format: daiFormat,
        adTagParameters: daiAdTagParameters,
      };

      ssaiAdsRequest = vodSsaiAdsRequest;
    }

    if (daiApiKey) {
      ssaiAdsRequest = {
        ...ssaiAdsRequest,
        apiKey: daiApiKey,
      };
    }

    if (instanceOfVodSSAIAdsRequest(ssaiAdsRequest)) {
      const vodRequest: VodSsaiAdsRequest = ssaiAdsRequest as VodSsaiAdsRequest;

      requestVODStream(
        vodRequest.contentSourceId,
        vodRequest.videoId,
        vodRequest.apiKey,
        vodRequest.format,
        vodRequest.adsId,
        vodRequest.adTagParameters,
      );
    } else if (instanceOfLiveSSAIAdsRequest(ssaiAdsRequest)) {
      const liveRequest: LiveSsaiAdsRequest = ssaiAdsRequest as LiveSsaiAdsRequest;

      requestLiveStream(
        liveRequest.assetKey,
        liveRequest.apiKey,
        liveRequest.format,
        liveRequest.adsId,
        liveRequest.adTagParameters,
      );
    } else {
      reject('Could not identify consumption type');
      return;
    }
  }

  function requestVODStream(
    contentSourceId: string,
    videoId: string,
    apiKey: string | undefined,
    format: string,
    adsId: string | undefined,
    adTagParameters: { [key: string]: any } | undefined,
  ): void {
    const streamRequest = new imaSDK.dai.api.VODStreamRequest();
    streamRequest.contentSourceId = contentSourceId;
    streamRequest.videoId = videoId;
    streamRequest.apiKey = apiKey || '';
    streamRequest.format = format;
    streamRequest.adsId = adsId || '';
    streamRequest.adTagParameters = adTagParameters || null;

    logger.log('[DAI][MANAGER][VOD] AdTagParameters', adTagParameters);

    streamManager.requestStream(streamRequest);
  }

  function requestLiveStream(
    assetKey: string,
    apiKey: string | undefined,
    format: string,
    adsId: string | undefined,
    adTagParameters: { [key: string]: any } | undefined,
  ): void {
    const streamRequest = new imaSDK.dai.api.LiveStreamRequest();
    streamRequest.assetKey = assetKey;
    streamRequest.apiKey = apiKey || '';
    streamRequest.format = format;
    streamRequest.adsId = adsId || '';
    streamRequest.adTagParameters = adTagParameters || null;

    logger.log('[DAI][MANAGER][Live] AdTagParameters', adTagParameters);

    streamManager.requestStream(streamRequest);
  }

  function createWrapperStreamManager(): any {
    let streamId: string | undefined = undefined;
    let cuepoints: any[] = [];
    streamManager.addEventListener(
      [imaSDK.dai.api.StreamEvent.Type.LOADED],
      (event: any) => {
        streamId = event.getStreamData().streamId;
        logger.log('[DAI] stream id', streamId);
      },
      false,
    );
    streamManager.addEventListener(
      [imaSDK.dai.api.StreamEvent.Type.CUEPOINTS_CHANGED],
      (event: any) => {
        cuepoints = event.data.cuepoints;
        logger.log('[DAI] cue points', cuepoints);
      },
      false,
    );

    return {
      streamManager: streamManager,
      streamId: (): string | undefined => {
        return streamId;
      },
      cuepoints: (): any => {
        return cuepoints;
      },
      isLive: (): boolean => {
        return !!daiAssetKey;
      },
      getAdContainerElement: (): Element => {
        return builder.adElement;
      },
      getAdTagParameters: (): { [key: string]: any } | undefined => {
        return ssaiAdsRequest?.adTagParameters;
      },
    };
  }

  function onStreamManagerEvent(event: any, resolve: any): void {
    if (event.type === imaSDK.dai.api.StreamEvent.Type.CUEPOINTS_CHANGED) {
      logger.log('[DAI] CUE Changed', event);
    }

    if (event.type === imaSDK.dai.api.StreamEvent.Type.LOADED) {
      logger.log('[DAI] stream loaded', event);
      const streamData = event.getStreamData();

      logger.log('[DAI][STREAM_EVENT] stream data', streamData.cuepoints);

      resolve({ streamManager: wrapperStreamManager, streamUrl: streamData.url });
    }

    if (event.type === imaSDK.dai.api.StreamEvent.Type.ERROR) {
      const fallbackReason = createAdPlaybackFailure(event);
      logger.log('[DAI] Ad intialization error, playback continue with fallback', fallbackReason);
      fallbackData = { ...wrapperStreamManager, ...{ isFallback: true, reason: fallbackReason } };

      resolve({ streamManager: fallbackData, streamUrl: fallbackUrl });
    }
  }

  return {
    getDaiMetadata(): Promise<DaiStreamMetadata> {
      return new Promise((resolve, reject) => {
        setupImaDai(resolve, reject);
      });
    },
  };
}
